import {RoomsFilter} from "../constants/rcn_rooms_filter";
import {RcnPriceStatsGrain} from "../types/IRcnPriceStats";
import {RcnPriceStatsQuery, RcnStatsTarget} from "../types/RcnPriceStatsQuery";
import {RcnPriceStatsResponse} from "../types/RcnPriceStatsResponse";
import {RcnPriceStatsSeriesPoint} from "../types/RcnPriceStatsSeriesPoint";
import {getPublishableRcnPoint} from "./get_publishable_rcn_point";
import {getRcnLatestGrainQuery, RCN_LATEST_GRAINS} from "./get_rcn_params";

/*
 * The address's newest published point - one rule for the server prefetch, the page hooks, the meta description and
 * the prices search dropdown:
 *
 * - RCN is asked for the newest point of every grain (month, quarter, year), with no date bounds, however old it is -
 *   market omitted, so the registry answers with both markets combined (see get_rcn_params).
 * - The point whose period starts latest wins. On a tie (e.g. January 2025, Q1 2025 and 2025 all start on
 *   2025-01-01) the finer grain wins: month over quarter over year.
 * - A year point is the availability probe: a month or a quarter with the registry's three sales means its year has
 *   them too, so an address (and rooms filter) without a year point has no point at all.
 * - A grain whose request failed is left out of the pick instead of being read as "no data" - see
 *   IRcnLatestPointResult.failedGrains for what callers do with such a partial answer.
 */

export interface IRcnResolvedPoint {
    point: RcnPriceStatsSeriesPoint;
    grain: RcnPriceStatsGrain;
}

/** The per m² queries of the newest point of each grain, in RCN_LATEST_GRAINS order: month, quarter, year. */
export const getRcnLatestGrainQueries = (target: RcnStatsTarget, rooms: RoomsFilter): RcnPriceStatsQuery[] =>
    RCN_LATEST_GRAINS.map((grain) => getRcnLatestGrainQuery(target, rooms, "per_m2", grain));

/** The year query alone - the availability probe. */
export const getRcnLatestYearQuery = (target: RcnStatsTarget, rooms: RoomsFilter): RcnPriceStatsQuery =>
    getRcnLatestGrainQuery(target, rooms, "per_m2", "year");

export const RCN_GRAIN_FAILED = "failed";

/** One grain's answer: the response, RCN_GRAIN_FAILED when its request failed, or undefined while it is still pending. */
export type RcnGrainAnswer = RcnPriceStatsResponse | typeof RCN_GRAIN_FAILED | undefined;

/**
 * A grain's answer from an RTK Query state - a hook result (pass its `currentData` as `data`) or a cache selector result.
 * A query that is being retried after a failure still counts as failed: RTK Query keeps the entry's last `error`
 * while the retry is pending, and reading it as "pending" instead would swap an already rendered point (e.g. one
 * the server rendered from the grains that answered) for a loader until the retry settles.
 */
export const toRcnGrainAnswer = (queryState: {data?: RcnPriceStatsResponse; isError: boolean; error?: unknown}): RcnGrainAnswer =>
    queryState.isError || (queryState.data === undefined && queryState.error !== undefined) ? RCN_GRAIN_FAILED : queryState.data;

/**
 * Picks the newest point from the answers of RCN_LATEST_GRAINS (same order). "pending" while any answer is still
 * missing - an unanswered grain could still hold the newest point. Failed grains are skipped; null when no answered
 * grain publishes.
 */
export const pickRcnLatestPoint = (answers: readonly RcnGrainAnswer[]): IRcnResolvedPoint | null | "pending" => {
    let latest: IRcnResolvedPoint | null = null;

    for (const [index, grain] of RCN_LATEST_GRAINS.entries()) {
        const answer = answers[index];

        if (answer === undefined) {
            return "pending";
        }

        const point = answer === RCN_GRAIN_FAILED ? null : getPublishableRcnPoint(answer);

        // Grains go from the finest, so only a strictly later start replaces the pick - a tie keeps the finer grain.
        if (point && (!latest || point.period_start > latest.point.period_start)) {
            latest = {point, grain};
        }
    }

    return latest;
};

export interface IRcnLatestPointResult {
    resolved: IRcnResolvedPoint | null;
    /**
     * Grains whose request failed after the year probe answered. `resolved` is then the newest point of the grains
     * that did answer and may be older than the address's real newest point. The address page shows it (the hooks
     * retry the failed grain in the browser); the prices search shows no price rather than a possibly stale one.
     */
    failedGrains: RcnPriceStatsGrain[];
}

/**
 * Resolves the newest point with `fetchRcn`: the year probe first - no year point, no point at all - then month and
 * quarter in parallel. Rejects when the year probe does: an unreachable registry says nothing about the address, so
 * it must not read as "no data". Makes the very requests the page hooks make (getRcnLatestGrainQueries), so a
 * server prefetch through here leaves the hooks nothing to fetch.
 */
export const fetchRcnLatestPoint = async (
    fetchRcn: (query: RcnPriceStatsQuery) => Promise<RcnPriceStatsResponse>,
    target: RcnStatsTarget,
    rooms: RoomsFilter
): Promise<IRcnLatestPointResult> => {
    const [monthQuery, quarterQuery, yearQuery] = getRcnLatestGrainQueries(target, rooms);
    const year = await fetchRcn(yearQuery);

    if (!getPublishableRcnPoint(year)) {
        return {resolved: null, failedGrains: []};
    }

    const finer = await Promise.allSettled([fetchRcn(monthQuery), fetchRcn(quarterQuery)]);
    const [month, quarter] = finer.map((result): RcnGrainAnswer => (result.status === "fulfilled" ? result.value : RCN_GRAIN_FAILED));
    const failedGrains = RCN_LATEST_GRAINS.filter((_grain, index) => [month, quarter, year][index] === RCN_GRAIN_FAILED);
    const resolved = pickRcnLatestPoint([month, quarter, year]);

    return {resolved: resolved === "pending" ? null : resolved, failedGrains};
};
import {RoomsFilter} from "../constants/rcn_rooms_filter";
import {RcnPriceStatsGrain} from "../types/IRcnPriceStats";
import {RcnPriceStatsQuery, RcnStatsTarget} from "../types/RcnPriceStatsQuery";
import {RcnPriceStatsResponse} from "../types/RcnPriceStatsResponse";
import {RcnPriceStatsSeriesPoint} from "../types/RcnPriceStatsSeriesPoint";
import {getPublishableRcnPoint} from "./get_publishable_rcn_point";
import {getRcnLatestGrainQuery, RCN_LATEST_GRAINS} from "./get_rcn_params";

/*
 * The address's newest published point - one rule for the server prefetch, the page hooks, the meta description and
 * the prices search dropdown:
 *
 * - RCN is asked for the newest point of every grain (month, quarter, year), with no date bounds, however old it is -
 *   market omitted, so the registry answers with both markets combined (see get_rcn_params).
 * - The point whose period starts latest wins. On a tie (e.g. January 2025, Q1 2025 and 2025 all start on
 *   2025-01-01) the finer grain wins: month over quarter over year.
 * - A year point is the availability probe: a month or a quarter with the registry's three sales means its year has
 *   them too, so an address (and rooms filter) without a year point has no point at all.
 * - A grain whose request failed is left out of the pick instead of being read as "no data" - see
 *   IRcnLatestPointResult.failedGrains for what callers do with such a partial answer.
 */

export interface IRcnResolvedPoint {
    point: RcnPriceStatsSeriesPoint;
    grain: RcnPriceStatsGrain;
}

/** The per m² queries of the newest point of each grain, in RCN_LATEST_GRAINS order: month, quarter, year. */
export const getRcnLatestGrainQueries = (target: RcnStatsTarget, rooms: RoomsFilter): RcnPriceStatsQuery[] =>
    RCN_LATEST_GRAINS.map((grain) => getRcnLatestGrainQuery(target, rooms, "per_m2", grain));

/** The year query alone - the availability probe. */
export const getRcnLatestYearQuery = (target: RcnStatsTarget, rooms: RoomsFilter): RcnPriceStatsQuery =>
    getRcnLatestGrainQuery(target, rooms, "per_m2", "year");

export const RCN_GRAIN_FAILED = "failed";

/** One grain's answer: the response, RCN_GRAIN_FAILED when its request failed, or undefined while it is still pending. */
export type RcnGrainAnswer = RcnPriceStatsResponse | typeof RCN_GRAIN_FAILED | undefined;

/**
 * A grain's answer from an RTK Query state - a hook result (pass its `currentData` as `data`) or a cache selector result.
 * A query that is being retried after a failure still counts as failed: RTK Query keeps the entry's last `error`
 * while the retry is pending, and reading it as "pending" instead would swap an already rendered point (e.g. one
 * the server rendered from the grains that answered) for a loader until the retry settles.
 */
export const toRcnGrainAnswer = (queryState: {data?: RcnPriceStatsResponse; isError: boolean; error?: unknown}): RcnGrainAnswer =>
    queryState.isError || (queryState.data === undefined && queryState.error !== undefined) ? RCN_GRAIN_FAILED : queryState.data;

/**
 * Picks the newest point from the answers of RCN_LATEST_GRAINS (same order). "pending" while any answer is still
 * missing - an unanswered grain could still hold the newest point. Failed grains are skipped; null when no answered
 * grain publishes.
 */
export const pickRcnLatestPoint = (answers: readonly RcnGrainAnswer[]): IRcnResolvedPoint | null | "pending" => {
    let latest: IRcnResolvedPoint | null = null;

    for (const [index, grain] of RCN_LATEST_GRAINS.entries()) {
        const answer = answers[index];

        if (answer === undefined) {
            return "pending";
        }

        const point = answer === RCN_GRAIN_FAILED ? null : getPublishableRcnPoint(answer);

        // Grains go from the finest, so only a strictly later start replaces the pick - a tie keeps the finer grain.
        if (point && (!latest || point.period_start > latest.point.period_start)) {
            latest = {point, grain};
        }
    }

    return latest;
};

export interface IRcnLatestPointResult {
    resolved: IRcnResolvedPoint | null;
    /**
     * Grains whose request failed after the year probe answered. `resolved` is then the newest point of the grains
     * that did answer and may be older than the address's real newest point. The address page shows it (the hooks
     * retry the failed grain in the browser); the prices search shows no price rather than a possibly stale one.
     */
    failedGrains: RcnPriceStatsGrain[];
}

/**
 * Resolves the newest point with `fetchRcn`: the year probe first - no year point, no point at all - then month and
 * quarter in parallel. Rejects when the year probe does: an unreachable registry says nothing about the address, so
 * it must not read as "no data". Makes the very requests the page hooks make (getRcnLatestGrainQueries), so a
 * server prefetch through here leaves the hooks nothing to fetch.
 */
export const fetchRcnLatestPoint = async (
    fetchRcn: (query: RcnPriceStatsQuery) => Promise<RcnPriceStatsResponse>,
    target: RcnStatsTarget,
    rooms: RoomsFilter
): Promise<IRcnLatestPointResult> => {
    const [monthQuery, quarterQuery, yearQuery] = getRcnLatestGrainQueries(target, rooms);
    const year = await fetchRcn(yearQuery);

    if (!getPublishableRcnPoint(year)) {
        return {resolved: null, failedGrains: []};
    }

    const finer = await Promise.allSettled([fetchRcn(monthQuery), fetchRcn(quarterQuery)]);
    const [month, quarter] = finer.map((result): RcnGrainAnswer => (result.status === "fulfilled" ? result.value : RCN_GRAIN_FAILED));
    const failedGrains = RCN_LATEST_GRAINS.filter((_grain, index) => [month, quarter, year][index] === RCN_GRAIN_FAILED);
    const resolved = pickRcnLatestPoint([month, quarter, year]);

    return {resolved: resolved === "pending" ? null : resolved, failedGrains};
};
